import { useQueryClient } from '@tanstack/react-query';
import { Messaging, MessagePayload, onMessage } from 'firebase/messaging';
import { useEffect, useRef } from 'react';
import { updateUserFCMToken as updateUserFCMTokenCall } from '../api';
import { USER_CONVERSATION_ID } from '../api/conversations.api';
import { queryKeys } from '../api/queryKeys';
import { messaging, reportError } from '../helpers';
import { announceConversationMessage } from '../helpers/conversationNotification';
import { getDevicePushToken } from '../helpers/devicePushToken';
import {
  isSystemProblemPush,
  showSystemProblemNotification,
} from '../helpers/systemProblemNotification';
import { useAuth } from '../store/auth';

/**
 * The ids, and the kind of conversation, travel as a JSON string under
 * `content` rather than as fields of their own - so this parse is what the
 * deep link and the cache keys are built from. Best-effort: a shape we do not
 * recognise still gets a notification, just a generically tagged one.
 */
const pushContent = (
  data?: Record<string, string>
): Record<string, unknown> => {
  try {
    return data?.content ? JSON.parse(data.content) : {};
  } catch {
    return {};
  }
};

export const useFirebaseNotification = () => {
  const user = useAuth((state) => state.user);
  const updateUser = useAuth((state) => state.updateUser);
  const updateUserEmailVerification = useAuth(
    (state) => state.updateUserEmailVerification
  );
  const queryClient = useQueryClient();

  /**
   * The effect below runs once per signed-in user, so everything it reaches
   * for goes through a ref - otherwise it would close over the first render's
   * values and, in a tab where someone signs out and back in as a colleague,
   * register the wrong account's device.
   */
  const latest = useRef({ user, updateUser, updateUserEmailVerification });
  latest.current = { user, updateUser, updateUserEmailVerification };

  /**
   * Register this browser as one of the user's devices.
   *
   * The backend now keeps one row per device rather than one per user, so the
   * question is no longer "is this the user's token" but "is this device
   * already in the list" - a user signed in on a phone and a laptop has two,
   * and comparing against the scalar `fcmToken` (which is only ever the most
   * recently registered device) would make one of them re-register forever.
   *
   * The response carries the authoritative list, so it is written back to the
   * store rather than guessed at. Writing the local value optimistically -
   * which is what this did before - meant a failed PUT left the client
   * believing it was registered and silently receiving nothing from then on.
   */
  const syncFcmToken = async () => {
    try {
      const currentToken = await getDevicePushToken();
      if (!currentToken) return;

      const registered = latest.current.user?.fcmTokens ?? [];
      if (registered.includes(currentToken)) return;

      try {
        const { data } = await updateUserFCMTokenCall(currentToken);
        latest.current.updateUser(data.data);
      } catch (error) {
        reportError(error);
      }
    } catch (err) {
      console.error('FCM sync error:', err);
    }
  };

  /**
   * A conversation push, in either shape the backend might send it.
   *
   * The spec names the type `conversationMessage` and describes the data
   * payload as `{ conversationId, messageId, type }`, but does not promise the
   * type travels inside `data` - so match on the id pair too. Requiring BOTH
   * ids keeps other push types out.
   *
   * Mirrors `isConversationPush` in
   * `apps/partner/src/firebase-messaging-sw.template.js`; the two cannot share
   * code (one runs in a service worker).
   */
  const isConversationPush = (data?: Record<string, string>) =>
    !!data &&
    (data.type === 'conversationMessage' ||
      (!!data.conversationId && !!data.messageId));

  /**
   * A restaurant referral push: the restaurant this location invited signed
   * up, or both bonuses were paid. Mirrors `isReferralPush` in the service
   * worker.
   */
  const isRestaurantReferralPush = (data?: Record<string, string>) =>
    data?.type === 'referralSignedUp' || data?.type === 'referralEarned';

  /**
   * Both delivery paths funnel through here.
   *
   * The existing behaviour - refreshing email verification on *any* push - is
   * deliberately kept as the default: it is load-bearing and the payloads that
   * drive it carry no type we can match on.
   */
  const handlePush = (data?: Record<string, string>) => {
    if (isConversationPush(data)) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.unreadCount,
      });
      const content = pushContent(data);
      /*
       * A merchant group is cached under its own id and listed in the Groups
       * tab, so those two go stale. Anything else is the support thread,
       * keyed on the sentinel rather than on `data.conversationId`: the
       * merchant has exactly one and its routes carry no id, so that is the
       * key its cache lives under. The admin app (which runs this hook too,
       * now that it registers for push) drives its chat caches from the
       * socket path - for it these are harmless extra invalidations.
       */
      if (content.type === 'merchantGroup' && content.conversationId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversations.messages(
            content.conversationId as number | string
          ),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversations.groupsAll,
        });
        return;
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.messages(USER_CONVERSATION_ID),
      });
      return;
    }

    /* A critical system problem opened. The Dashboard card and list follow
       it over their own socket while open; this covers a tab where that
       socket is down. */
    if (isSystemProblemPush(data)) {
      queryClient.invalidateQueries({ queryKey: queryKeys.systemProblems.all });
      return;
    }

    /* The Refer a Restaurant page is the one that changed. The balance moved
       too when the bonus was paid, but that screen refetches on its own. */
    if (isRestaurantReferralPush(data)) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.restaurantReferrals.all,
      });
      return;
    }

    latest.current.updateUserEmailVerification();
  };

  /**
   * Show the notification ourselves when the push arrives with the app open.
   *
   * FCM only displays one when NO window of the app is visible. A merchant with
   * the dashboard on one screen and their POS on the other counts as visible,
   * so the browser stayed silent and the only sign of a new message was the
   * sidebar badge - which is exactly the case that needs announcing.
   *
   * The socket announces that same case, instantly and without the backend's
   * per-conversation throttle, so in practice this is the fallback for a
   * moment when the socket is down or still reconnecting. Both go through the
   * one helper, and share its tag: whichever arrives second replaces the first
   * instead of stacking under it.
   *
   * Never called for background pushes - those come through the service
   * worker's own path, which has already displayed one.
   */
  const showForegroundNotification = async (payload: MessagePayload) => {
    const data = payload.data ?? {};
    const content = pushContent(data);

    await announceConversationMessage({
      /*
       * `notification` first while it is still sent, `data` once the backend
       * stops sending it: a data-only push carries its own text and is the
       * only thing there is to read. Same order as the service worker's, so
       * the two paths cannot describe one message differently. Viktor's
       * push is titled "Viktor" by the backend, but on screen he is vGrubs AI.
       */
      title:
        content.type === 'viktor'
          ? 'vGrubs AI'
          : payload.notification?.title || data.title || 'vGrubs',
      body:
        payload.notification?.body ||
        data.body ||
        data.preview ||
        'New message',
      conversationId: (content.conversationId ?? data.conversationId) as
        | number
        | string
        | undefined,
      messageId: (content.messageId ?? data.messageId) as
        | number
        | string
        | undefined,
      /* The toast opens the group the push is about, not the support thread. */
      groupId:
        content.type === 'merchantGroup'
          ? Number(content.conversationId) || null
          : null,
      data,
    });
  };

  /** The foreground path: refresh what is on screen, then announce it. */
  const handleForeground = (payload: MessagePayload) => {
    handlePush(payload.data);
    if (isSystemProblemPush(payload.data)) {
      showSystemProblemNotification({
        title: payload.notification?.title || 'System problem',
        body: payload.notification?.body || '',
        data: payload.data ?? {},
      }).catch(reportError);
      return;
    }
    if (!isConversationPush(payload.data)) return;
    showForegroundNotification(payload).catch(reportError);
  };

  const handlePushRef = useRef(handlePush);
  handlePushRef.current = handlePush;

  const handleForegroundRef = useRef(handleForeground);
  handleForegroundRef.current = handleForeground;

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    if (!user?.id) return;

    syncFcmToken();

    // Foreground messages - fired only while a window of the app is visible.
    const unsubscribe = onMessage(messaging as Messaging, (payload) => {
      handleForegroundRef.current(payload);
    });

    // Background messages, forwarded by the service worker.
    const onServiceWorkerMessage = (event: MessageEvent) =>
      handlePushRef.current(event.data?.payload);

    navigator.serviceWorker.addEventListener('message', onServiceWorkerMessage);

    return () => {
      unsubscribe();
      // This listener used to leak: only `unsubscribe()` was returned, so
      // every remount added another handler.
      navigator.serviceWorker.removeEventListener(
        'message',
        onServiceWorkerMessage
      );
    };
    // Re-runs when a different account signs in on this browser - the token
    // has to move to them, and the backend does that on registration.
  }, [user?.id]);
};
